import {
  type DocumentData,
  type QueryConstraint,
  Timestamp,
  collection,
  deleteField,
  doc,
  getCountFromServer,
  limit,
  onSnapshot,
  orderBy,
  query,
  serverTimestamp,
  setDoc,
  updateDoc,
  where,
  writeBatch,
} from 'firebase/firestore';

import { getBytes, ref, uploadBytesResumable } from 'firebase/storage';

import { getFirebaseAuth, getFirebaseDb, getFirebaseStorage } from '../app';
import {
  addDocument,
  getDocument,
  getDocuments,
  subscribeToCollection,
  subscribeToDocument,
  updateDocument,
} from '../firestore';

const COLLECTION = 'jobs';

export async function getChart(chartId: string): Promise<(DocumentData & { id: string }) | null> {
  return getDocument(COLLECTION, chartId);
}

export async function getChartsByUser(userId: string): Promise<(DocumentData & { id: string })[]> {
  return getDocuments(COLLECTION, where('userId', '==', userId), orderBy('createdAt', 'desc'));
}

export async function createChart(data: Record<string, unknown>): Promise<string> {
  return addDocument(COLLECTION, data);
}

export async function updateChart(chartId: string, data: Record<string, unknown>): Promise<void> {
  return updateDocument(COLLECTION, chartId, data);
}

export async function deleteChart(chartId: string): Promise<void> {
  return softDeleteChart(chartId);
}

// Soft delete (originally CAB-229; regressed by the CAB-54 onboarding merge and
// restored here): the end user stops seeing the chart, but the jobs/{id} doc and
// its admin-facing metadata REMAIN in Firestore. We flag it deleted, rename the
// title, and scrub the PHI/clinical content. The `deleted` flag flip is what the
// backend `jobs` onUpdate Cloud Function watches to cascade audio-subdoc
// tombstones + storage cleanup, so no hard delete is performed here.
export async function softDeleteChart(chartId: string): Promise<void> {
  return updateDocument(COLLECTION, chartId, {
    deleted: true,
    deletedAt: serverTimestamp(),
    title: 'Deleted Chart',
    summary: deleteField(),
    transcript: deleteField(),
    summaryGPT: deleteField(),
    summaryGemini: deleteField(),
    summaryGemini2: deleteField(),
    summaryGemini3: deleteField(),
    summaryClaude: deleteField(),
    llmOutput: deleteField(),
    segments: deleteField(),
  });
}

export function subscribeToChartsByProfile(
  userId: string,
  profileId: string,
  callback: (docs: (DocumentData & { id: string })[]) => void,
): () => void {
  return subscribeToCollection(
    COLLECTION,
    [where('uid', '==', userId), where('profileUID', '==', profileId)],
    callback,
  );
}

export function subscribeToChart(
  chartId: string,
  callback: (chart: (DocumentData & { id: string }) | null) => void,
): () => void {
  return subscribeToDocument(COLLECTION, chartId, callback);
}

/**
 * The current clinical note, read from the source of truth.
 *
 * CAB-540: the note lives in the `jobs/{chartId}/summary` subcollection, one
 * `SummaryHistoryEntry` per change, and the entry with the latest `createdAt`
 * is the current note. The backend appends here on every pipeline run AND on
 * every root-`summary` change (incl. admin edits from the Angular dashboard),
 * so this is the freshest, canonical text — the root-doc `summary` /
 * `notesByTemplate` can lag behind an admin edit.
 */
export interface LatestSummary {
  id: string;
  summary: string;
  /** Firestore Timestamp; left loose so callers don't import the type. */
  createdAt: unknown;
  modifiedBy: string | null;
  source: string | null;
}

/**
 * Subscribe to the latest `jobs/{chartId}/summary` entry (by `createdAt`).
 *
 * Calls back with `null` when the subcollection is empty — i.e. a legacy chart
 * whose note only ever lived in the root doc, or a brand-new chart with no note
 * yet. Callers fall back to the root-doc note in that case.
 */
export function subscribeToLatestSummary(
  chartId: string,
  callback: (summary: LatestSummary | null) => void,
  onError?: (error: Error) => void,
): () => void {
  const db = getFirebaseDb();
  const q = query(
    collection(db, COLLECTION, chartId, 'summary'),
    orderBy('createdAt', 'desc'),
    limit(1),
  );

  return onSnapshot(
    q,
    (snapshot) => {
      const docSnap = snapshot.docs[0];
      if (!docSnap) {
        callback(null);
        return;
      }
      const data = docSnap.data();
      const summaryVal = data['summary'];
      const modifiedByVal = data['modifiedBy'];
      const sourceVal = data['source'];
      callback({
        id: docSnap.id,
        summary: typeof summaryVal === 'string' ? summaryVal : '',
        createdAt: data['createdAt'] ?? null,
        modifiedBy: typeof modifiedByVal === 'string' ? modifiedByVal : null,
        source: typeof sourceVal === 'string' ? sourceVal : null,
      });
    },
    // CAB-544: the summary subcollection read is owner-gated in Firestore rules
    // (jobs/{id}.uid == auth.uid). A denied read — or any listener failure —
    // arrives here asynchronously, NOT as a throw from onSnapshot itself. Without
    // this handler the error was uncaught (console: permission-denied) and the
    // page never fell back to the root-doc note. Report null so callers degrade
    // to the root-doc note, and forward the error so the hook can flag isError.
    (error) => {
      console.error('subscribeToLatestSummary: summary listener failed', error);
      callback(null);
      onError?.(error);
    },
  );
}

export function subscribeToAllChartsByUser(
  userId: string,
  callback: (docs: (DocumentData & { id: string })[]) => void,
): () => void {
  return subscribeToCollection(COLLECTION, [where('uid', '==', userId)], callback);
}

export async function getChartsByPatient(
  _userId: string,
  patientId: string,
): Promise<(DocumentData & { id: string })[]> {
  return getDocuments(COLLECTION, where('patientUID', '==', patientId));
}

export async function getAllChartsByUser(
  userId: string,
): Promise<(DocumentData & { id: string })[]> {
  return getDocuments(COLLECTION, where('uid', '==', userId));
}

/**
 * Count of jobs created at or after `since` for the given user.
 * Uses Firestore's server-side count aggregation — only the integer
 * comes back over the wire, no doc reads. Used by the StatsBanner
 * "Time Saved This Week" card (CAB-368).
 */
export async function countJobsSince(userId: string, since: Date): Promise<number> {
  const db = getFirebaseDb();
  const q = query(
    collection(db, COLLECTION),
    where('uid', '==', userId),
    where('createdAt', '>=', Timestamp.fromDate(since)),
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

export async function getChartsByDateRange(
  userId: string,
  profileId: string,
  _startDate: string,
  _endDate: string,
): Promise<(DocumentData & { id: string })[]> {
  // Query by uid + profileUID (matching old project's field names)
  // Date filtering done client-side since `date` is stored as a Firestore Timestamp
  const docs = await getDocuments(
    COLLECTION,
    where('uid', '==', userId),
    where('profileUID', '==', profileId),
  );
  return docs;
}

/**
 * Creates a chart entry in the `jobs` collection matching the old project's schema.
 */
export async function createChartEntry(data: {
  title: string;
  patientUID: string;
  uid: string;
  staffUID: string;
  profileUID: string;
  owner: string;
  summaryType: string;
  date: Date;
  requireConsent: boolean;
}): Promise<string> {
  const chartData: Record<string, unknown> = {
    title: data.title,
    patientUID: data.patientUID,
    uid: data.uid,
    staffUID: data.staffUID,
    profileUID: data.profileUID,
    owner: data.owner,
    summary_type: data.summaryType,
    date: data.date,
    createdAt: new Date(),
    status: 'No Audio',
    duration: 0,
    totalAudioDuration: 0,
    transcript: '',
    officeId: '',
    requireConsent: data.requireConsent,
    createdOnPlatform: 'web',
  };
  return addDocument(COLLECTION, chartData);
}

/**
 * Saves critical debug info to `jobs/{chartId}/debug/critical` subcollection.
 */
export async function saveCriticalDebugInfo(
  chartId: string,
  debugInfo: Record<string, unknown>,
): Promise<void> {
  const db = getFirebaseDb();
  const debugDocRef = doc(db, COLLECTION, chartId, 'debug', 'critical');
  await setDoc(debugDocRef, debugInfo);
}

/**
 * Records whether the user finished uploading the chart's audio, on
 * `jobs/{chartId}/debug/critical.userFinishedUploadingAudio` (CAB-502).
 *
 * Set `false` when recording starts and `true` once the upload completes. If a
 * job later errors while this is not `true`, the backend treats it as an
 * interrupted/incomplete upload and shows the user an explanatory message.
 *
 * Merges into the debug doc so the existing free-form debug blob is preserved.
 */
export async function setUploadFinishedFlag(chartId: string, finished: boolean): Promise<void> {
  const db = getFirebaseDb();
  const debugDocRef = doc(db, COLLECTION, chartId, 'debug', 'critical');
  await setDoc(debugDocRef, { userFinishedUploadingAudio: finished }, { merge: true });
}

/**
 * Collects browser/device debug info for troubleshooting.
 */
export function collectDebugInfo(): Record<string, unknown> {
  const nav = navigator as unknown as Record<string, unknown>;
  const conn = nav['connection'] as Record<string, unknown> | undefined;

  return {
    browser: getBrowserName(),
    browserVersion: getBrowserVersion(),
    os: getOSName(),
    userAgent: navigator.userAgent,
    language: navigator.language,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    screenResolution: `${screen.width}x${screen.height}`,
    devicePixelRatio: window.devicePixelRatio || 1,
    touchSupport: 'ontouchstart' in window || navigator.maxTouchPoints > 0,
    deviceType: getDeviceType(),
    networkType: conn?.['type'] ?? null,
    networkEffectiveType: conn?.['effectiveType'] ?? null,
    networkDownlink: conn?.['downlink'] ?? null,
    networkRTT: conn?.['rtt'] ?? null,
    collectedAt: new Date(),
  };
}

function getBrowserName(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Firefox')) return 'Firefox';
  if (ua.includes('Edg/')) return 'Edge';
  if (ua.includes('Chrome')) return 'Chrome';
  if (ua.includes('Safari')) return 'Safari';
  return 'Unknown';
}

function getBrowserVersion(): string {
  const ua = navigator.userAgent;
  const match = ua.match(/(Firefox|Edg|Chrome|Safari|Version)\/(\d+[\d.]*)/);
  return match?.[2] ?? 'Unknown';
}

function getOSName(): string {
  const ua = navigator.userAgent;
  if (ua.includes('Windows')) return 'Windows';
  if (ua.includes('Mac OS')) return 'macOS';
  if (ua.includes('Linux')) return 'Linux';
  if (ua.includes('Android')) return 'Android';
  if (ua.includes('iPhone') || ua.includes('iPad')) return 'iOS';
  return 'Unknown';
}

function getDeviceType(): string {
  const ua = navigator.userAgent;
  if (/Mobi|Android/i.test(ua)) return 'mobile';
  if (/Tablet|iPad/i.test(ua)) return 'tablet';
  return 'desktop';
}

/**
 * Uploads audio to Firebase Storage, creates audio subcollection doc,
 * and updates chart status to "Processing" — matching old project flow.
 */
export async function uploadChartAudio(input: {
  chartId: string;
  userId: string;
  audioBlob: Blob;
  onProgress?: (percent: number) => void;
}): Promise<{ storagePath: string }> {
  const timestamp = Date.now();
  const audioId = `${input.chartId}_${timestamp}`;
  const storagePath = `users/${input.userId}/uploads/${audioId}.m4a`;

  // 1. Upload to Firebase Storage with progress tracking
  const storage = getFirebaseStorage();
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, input.audioBlob);

  const snapshot = await new Promise<{ ref: { bucket: string; fullPath: string } }>(
    (resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snap) => {
          if (snap.totalBytes > 0 && input.onProgress) {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            input.onProgress(pct);
          }
        },
        reject,
        () => {
          resolve(uploadTask.snapshot);
        },
      );
    },
  );
  const bucket = snapshot.ref.bucket;
  const fullPath = snapshot.ref.fullPath;
  const audioGS = `gs://${bucket}/${fullPath}`;

  // 2. Batch: update chart status + create audio subcollection doc
  const db = getFirebaseDb();
  const batch = writeBatch(db);

  // Update parent chart status to "Processing"
  const chartRef = doc(db, COLLECTION, input.chartId);
  batch.update(chartRef, { status: 'Processing' });

  // Create audio subcollection doc at jobs/{chartId}/audio/{timestamp}
  // Matching exact fields from old project
  const audioDocRef = doc(db, COLLECTION, input.chartId, 'audio', String(timestamp));
  batch.set(audioDocRef, {
    name: `${timestamp}.m4a`,
    createdAt: new Date(),
    created: new Date(),
    storagePath: fullPath,
    audioGS,
    duration: 0,
    transcript: '',
  });

  // CAB-502: mark the upload as finished atomically with the audio doc/status,
  // so an errored job without this flag can be identified as an interrupted
  // upload. Merged so the existing debug/critical blob is preserved.
  const debugDocRef = doc(db, COLLECTION, input.chartId, 'debug', 'critical');
  batch.set(debugDocRef, { userFinishedUploadingAudio: true }, { merge: true });

  await batch.commit();

  return { storagePath: fullPath };
}

// Firebase Storage JS SDK (uploadBytesResumable AND uploadString) internally
// creates a Blob from an ArrayBuffer, which crashes on React Native 0.76+ across
// all devices. Bypass the SDK entirely and hit the REST API directly, sending a
// Uint8Array body which fetch handles natively without any Blob creation.
export async function uploadChartAudioBase64(input: {
  chartId: string;
  userId: string;
  base64: string;
  mimeType: string;
}): Promise<{ storagePath: string }> {
  const timestamp = Date.now();
  const audioId = `${input.chartId}_${timestamp}`;
  const storagePath = `users/${input.userId}/uploads/${audioId}.m4a`;

  const auth = getFirebaseAuth();
  const token = await auth.currentUser?.getIdToken();
  if (!token) {
    throw new Error('Not authenticated');
  }

  const storage = getFirebaseStorage();
  const bucket = storage.app.options['storageBucket'] as string;

  // Decode base64 → Uint8Array without creating a Blob
  const binary = atob(input.base64);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i++) {
    bytes[i] = binary.charCodeAt(i);
  }

  const encodedPath = encodeURIComponent(storagePath);
  const uploadUrl = `https://firebasestorage.googleapis.com/v0/b/${bucket}/o?uploadType=media&name=${encodedPath}`;

  const response = await fetch(uploadUrl, {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${token}`,
      'Content-Type': input.mimeType,
    },
    body: bytes,
  });

  if (!response.ok) {
    const text = await response.text().catch(() => String(response.status));
    throw new Error(`Storage upload failed (${String(response.status)}): ${text}`);
  }

  const fullPath = storagePath;
  const audioGS = `gs://${bucket}/${fullPath}`;

  const db = getFirebaseDb();
  const batch = writeBatch(db);

  const chartRef = doc(db, COLLECTION, input.chartId);
  batch.update(chartRef, { status: 'Processing' });

  const audioDocRef = doc(db, COLLECTION, input.chartId, 'audio', String(timestamp));
  batch.set(audioDocRef, {
    name: `${timestamp}.m4a`,
    createdAt: new Date(),
    created: new Date(),
    storagePath: fullPath,
    audioGS,
    duration: 0,
    transcript: '',
  });

  await batch.commit();

  return { storagePath: fullPath };
}

/**
 * Uploads audio as a checkpoint (for pause functionality).
 * Only saves to Storage and updates chart with checkpoint path.
 * Does NOT trigger transcription or change status to Processing.
 */
export async function uploadChartAudioCheckpoint(input: {
  chartId: string;
  userId: string;
  audioBlob: Blob;
  onProgress?: (percent: number) => void;
}): Promise<{ storagePath: string }> {
  const timestamp = Date.now();
  const audioId = `${input.chartId}_checkpoint_${timestamp}`;
  const storagePath = `users/${input.userId}/uploads/${audioId}.m4a`;

  // Upload to Firebase Storage with progress tracking
  const storage = getFirebaseStorage();
  const storageRef = ref(storage, storagePath);
  const uploadTask = uploadBytesResumable(storageRef, input.audioBlob);

  const snapshot = await new Promise<{ ref: { bucket: string; fullPath: string } }>(
    (resolve, reject) => {
      uploadTask.on(
        'state_changed',
        (snap) => {
          if (snap.totalBytes > 0 && input.onProgress) {
            const pct = Math.round((snap.bytesTransferred / snap.totalBytes) * 100);
            input.onProgress(pct);
          }
        },
        reject,
        () => {
          resolve(uploadTask.snapshot);
        },
      );
    },
  );
  const fullPath = snapshot.ref.fullPath;

  // Update chart with checkpoint path (don't change status or trigger processing)
  const db = getFirebaseDb();
  const chartRef = doc(db, COLLECTION, input.chartId);
  await updateDoc(chartRef, {
    audioCheckpointPath: fullPath,
    audioCheckpointAt: new Date(),
  });

  return { storagePath: fullPath };
}

/**
 * Downloads the checkpoint audio blob from Firebase Storage.
 * Returns null if no checkpoint exists.
 */
export async function downloadChartAudioCheckpoint(checkpointPath: string): Promise<Blob | null> {
  try {
    const storage = getFirebaseStorage();
    const storageRef = ref(storage, checkpointPath);
    const bytes = await getBytes(storageRef);
    // Determine MIME type from path extension
    const mimeType = checkpointPath.endsWith('.m4a') ? 'audio/mp4' : 'audio/webm';
    return new Blob([bytes], { type: mimeType });
  } catch (err) {
    console.warn('[chartService] Failed to download checkpoint:', err);
    return null;
  }
}

export async function getOrgChartsByDateRange(
  officeIds: string[],
  startDate: string,
  endDate: string,
): Promise<(DocumentData & { id: string })[]> {
  if (officeIds.length === 0) {
    return [];
  }

  // Firestore 'in' queries support up to 30 values
  const batchSize = 30;
  const results: (DocumentData & { id: string })[] = [];

  for (let i = 0; i < officeIds.length; i += batchSize) {
    const batch = officeIds.slice(i, i + batchSize);
    const constraints: QueryConstraint[] = [
      where('officeId', 'in', batch),
      where('date', '>=', startDate),
      where('date', '<=', endDate),
      orderBy('date', 'asc'),
    ];
    const batchResults = await getDocuments(COLLECTION, ...constraints);
    results.push(...batchResults);
  }

  return results;
}
